import React, { useEffect, useRef, useState, useCallback } from "react";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { getSocket } from "@/lib/socket";

export default function AdminChat() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try { const { data } = await api.get("/admin/chat/threads"); setThreads(data); }
    catch (e) { console.warn("Failed to load chat threads", e); } finally { setLoading(false); }
  }, []);
  const loadMsgs = useCallback(async (cid) => {
    if (!cid) return;
    try { const { data } = await api.get(`/admin/chat/messages?customer_id=${encodeURIComponent(cid)}`); setMsgs(data); }
    catch (e) { console.warn("Failed to load chat messages", e); }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { loadMsgs(active); }, [active, loadMsgs]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  useEffect(() => {
    const s = getSocket();
    const onMsg = (m) => { loadThreads(); if (active && m.customer_id === active) loadMsgs(active); };
    s.on("chat_message", onMsg);
    return () => s.off("chat_message", onMsg);
  }, [active, loadThreads, loadMsgs]);

  const send = async () => {
    const t = text.trim();
    if (!t || !active) return;
    setText("");
    try { await api.post("/admin/chat/messages", { customer_id: active, text: t }); loadMsgs(active); loadThreads(); }
    catch (e) { console.error("Failed to send chat message", e); }
  };

  const activeThread = threads.find((t) => t.customer_id === active);

  return (
    <div>
      <h1 className="font-heading font-extrabold text-2xl mb-4 flex items-center gap-2"><MessageSquare className="text-metros-red" /> Customer Chat</h1>
      <div className="grid md:grid-cols-3 gap-4 h-[70vh]">
        <div className="bg-white rounded-2xl border border-gray-100 overflow-y-auto" data-testid="chat-threads">
          {loading ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-metros-red" /></div>
            : threads.length === 0 ? <p className="text-center text-sm text-gray-400 py-10">No conversations yet.</p>
            : threads.map((t) => (
              <button key={t.customer_id} data-testid={`chat-thread-${t.customer_id}`} onClick={() => setActive(t.customer_id)}
                className={`w-full text-left px-4 py-3 border-b border-gray-50 ${active === t.customer_id ? "bg-metros-red/5" : "hover:bg-gray-50"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm">{t.customer_name || "Customer"}</span>
                  {t.unread > 0 && <span className="bg-metros-red text-white text-xs rounded-full px-2 py-0.5">{t.unread}</span>}
                </div>
                <p className="text-xs text-gray-500 truncate">{t.last_sender === "admin" ? "You: " : ""}{t.last_text}</p>
              </button>
            ))}
        </div>
        <div className="md:col-span-2 bg-white rounded-2xl border border-gray-100 flex flex-col">
          {!active ? <div className="flex-1 flex items-center justify-center text-sm text-gray-400">Select a conversation</div> : (
            <>
              <div className="px-4 py-3 border-b border-gray-100 font-semibold">{activeThread?.customer_name || "Customer"}</div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
                {msgs.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.sender === "admin" ? "bg-metros-red text-white rounded-br-sm" : "bg-white border border-gray-200 rounded-bl-sm"}`}>{m.text}</div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="p-2 border-t border-gray-100 flex items-center gap-2">
                <input data-testid="admin-chat-input" value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a reply…"
                  className="flex-1 rounded-full border border-gray-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-metros-red" />
                <button data-testid="admin-chat-send" onClick={send} className="w-10 h-10 rounded-full bg-metros-red text-white flex items-center justify-center shrink-0"><Send size={16} /></button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
